import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` },
});

const CHANNELS = ["edisplay", "funeral", "infinity"];
const CHANNEL_LABEL = { edisplay: "e-Displayroom", funeral: "Funeral", infinity: "Infinity" };

// Channel override is 'include' | 'exclude' | null(inherit). UI uses "inherit" for null.
const ovToUi = (v) => (v == null ? "inherit" : v);
const uiToOv = (v) => (v === "inherit" ? null : v);

/**
 * Per-product channel availability popup (Product Management v3).
 * Lets an admin set the Include / Exclude / Inherit override for each of the
 * three sales channels. Overrides win over the product's collection tags and
 * survive collection re-tagging. Prices are managed separately in the product
 * edit form's Pricing tab.
 *
 * Wired to PUT /admin/products/:id/channels. Shared by the Master Catalog page
 * and the unified Product Management → Products page.
 *
 * Props:
 *   - product:  the product row ({ product_id, name, channel_overrides }) or null
 *   - open:     boolean
 *   - onClose:  () => void
 *   - onSaved:  (effective_channels: string[]) => void   // caller refreshes its list
 */
export default function ChannelOverrideDialog({ product, open, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (product) {
      setForm({
        edisplay: ovToUi(product.channel_overrides?.edisplay),
        funeral: ovToUi(product.channel_overrides?.funeral),
        infinity: ovToUi(product.channel_overrides?.infinity),
      });
    } else {
      setForm(null);
    }
  }, [product]);

  const save = async () => {
    if (!product || !form) return;
    setSaving(true);
    try {
      const payload = {
        channel_overrides: {
          edisplay: uiToOv(form.edisplay),
          funeral: uiToOv(form.funeral),
          infinity: uiToOv(form.infinity),
        },
      };
      const res = await axios.put(
        `${API}/admin/products/${product.product_id}/channels`,
        payload,
        getAuthHeaders()
      );
      const effective = res.data.effective_channels || [];
      toast.success(`Updated — channels: ${effective.join(", ") || "none"}`);
      onClose?.();
      onSaved?.(effective);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Channel overrides — {product?.name}</DialogTitle>
        </DialogHeader>
        {form && (
          <div className="space-y-4 mt-2">
            <p className="text-xs text-slate-500">
              <strong>Inherit</strong> = follow the product's collection tags.{" "}
              <strong>Include</strong>/<strong>Exclude</strong> force the channel on/off and
              survive collection re-tagging.
            </p>
            {CHANNELS.map((c) => (
              <div key={c} className="flex items-center justify-between gap-3">
                <Label className="w-28">{CHANNEL_LABEL[c]}</Label>
                <Select
                  value={form[c]}
                  onValueChange={(v) => setForm((prev) => ({ ...prev, [c]: v }))}
                >
                  <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inherit">Inherit (from collections)</SelectItem>
                    <SelectItem value="include">Include</SelectItem>
                    <SelectItem value="exclude">Exclude</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
            <p className="text-[11px] text-slate-400">
              Override prices are managed in the product's Pricing tab.
            </p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose?.()}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
